import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldOff, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";

export default function CandidateHome() {
  const { t, lang } = useI18n();
  const [a, setA] = useState(null);
  const [status, setStatus] = useState({ locked: false, is_weekend: false, weekday: "" });

  useEffect(() => {
    (async () => {
      try {
        const st = await api.get("/candidate/status");
        setStatus(st.data);
        if (!st.data.locked) {
          const r = await api.get("/candidate/assignment");
          setA(r.data);
        }
      } catch (err) {
        if (err?.response?.status === 423) setStatus((s) => ({ ...s, locked: true }));
      }
    })();
  }, []);

  if (status.locked) {
    return (
      <div className="rounded-2xl bg-white border border-rose-200 p-8 sm:p-10 max-w-2xl mx-auto text-center shadow-[0_8px_30px_-10px_rgba(225,29,72,0.25)]">
        <div className="w-14 h-14 rounded-2xl bg-rose-50 text-[var(--accent)] flex items-center justify-center mx-auto"><ShieldOff size={28} /></div>
        <h2 className="font-display text-2xl font-bold mt-4">Account Locked</h2>
        <p className="text-sm text-zinc-700 mt-3 leading-relaxed">Your account has been locked due to a violation of DataSprint Work Policies. Please contact the administrator for further assistance.</p>
      </div>
    );
  }

  if (!a) return <div className="text-zinc-500">{t("loading")}</div>;

  const items = [
    [t("name"), `${a.first_name} ${a.last_name}`],
    [t("email"), a.email],
    [t("start_date"), a.start_date],
    [t("last_date"), a.last_date],
    [`${t("total")} ${t("pages")}`, a.total_pages],
    [`${t("completed")} ${t("pages")}`, a.completed_pages],
    ["Total Days", a.total_days],
    ["Days Left", a.days_left],
  ];

  return (
    <div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{t("welcome")}, {a.first_name}!</h1>
      <p className="text-zinc-500 mt-2">{t("your_assignment")}</p>

      {status.is_weekend && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 mt-6 flex items-start gap-3 text-sm text-amber-800">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">{lang === "hi" ? "वीकेंड बंद" : "Weekend Closed"}</div>
            <div className="mt-1">Typing work is unavailable on weekends. Please return on the next working day.</div>
            <div className="mt-1 text-xs opacity-70">Server day: {status.weekday}</div>
          </div>
        </div>
      )}

      {a.expired && (
        <div className="mt-6 rounded-2xl bg-rose-50 border border-rose-200 p-5">
          <div className="font-display font-bold text-rose-700">{t("account_expired")}</div>
          <p className="text-sm text-rose-700 mt-1">{t("account_expired_msg")}</p>
        </div>
      )}

      <div className="mt-8 rounded-2xl bg-white border border-[var(--line)] overflow-hidden shadow-sm">
        <table className="w-full">
          <tbody>
            {items.map(([k, v]) => (
              <tr key={k} className="border-b border-[var(--line)] last:border-0">
                <td className="px-5 py-3 text-xs uppercase tracking-widest text-zinc-500 w-1/3">{k}</td>
                <td className="px-5 py-3 font-medium">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        {!a.expired && !status.is_weekend && <Link to="/dashboard/typing" data-testid="start-typing-btn" className="btn-primary">{t("start_typing")} →</Link>}
        {a.show_result && <Link to="/dashboard/results" data-testid="check-result-btn" className="btn-ghost">{t("check_result")}</Link>}
      </div>
    </div>
  );
}
